import { useEffect, useCallback, useRef } from 'react';

/**
 * Detects when the user scrolls near the bottom and calls `onLoadMore`.
 * Listens on `.swipe-content-wrapper` (the mobile scroll container) if present,
 * otherwise falls back to window scroll.
 *
 * @param {Object} opts
 * @param {boolean} opts.enabled - only listen when true (e.g. isMobile && isActive)
 * @param {boolean} opts.hasMore - whether more pages exist
 * @param {boolean} opts.loading - skip if already loading
 * @param {Function} opts.onLoadMore - callback to load the next page
 * @param {number} [opts.threshold=200] - px from bottom to trigger
 */
export default function useInfiniteScroll({ enabled, hasMore, loading, onLoadMore, threshold = 200 }) {
  const pendingRef = useRef(false);

  // Reset pending flag when loading state changes (i.e. the load-more was processed)
  useEffect(() => {
    if (!loading) {
      pendingRef.current = false;
    }
  }, [loading]);

  const handleScroll = useCallback((e) => {
    if (!enabled || loading || !hasMore || pendingRef.current) return;

    const el = e ? e.target : null;
    if (el && el.scrollHeight) {
      // Scrollable container
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < threshold) {
        pendingRef.current = true;
        onLoadMore();
      }
    } else {
      // Window fallback
      const scrollY = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (docHeight - scrollY - windowHeight < threshold) {
        pendingRef.current = true;
        onLoadMore();
      }
    }
  }, [enabled, hasMore, loading, onLoadMore, threshold]);

  useEffect(() => {
    if (!enabled) return;

    // Find the mobile scroll container
    const scrollContainer = document.querySelector('.swipe-content-wrapper');
    const target = scrollContainer || window;

    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleScroll);
  }, [enabled, handleScroll]);
}